import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, TextField, Typography, Divider, IconButton, InputAdornment } from "@mui/material";
import { Receipt } from "@mui/icons-material";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import CardTable from "../../components/CardTable/CardTable";
import { ModalV2 } from "../../components/Modal/ModalV2";
import { useForm } from "../../hooks/useForm";
import { useSearch } from "../../hooks/useSearch";
import { useValidateReload } from "../../hooks/useValidateReload";
import {
  setFormCancelInvoice,
  cancelInvoice,
  emailModal,
  forwardEmail,
  getInvoicesPaymentTypes,
  setFormSubstituteInvoice,
  substituteInvoice,
} from "../../redux/actions/invoices/invoicesAction";
import { getSatUsages } from "../../redux/actions/creditNotes/creditNotesActions";
import { AutocompleteComponent } from "../../components/Autocomplete/AutocompleteComponent";
import { regimensData } from "../../enums/regimens";
import { paymentsInvoicesType } from "../../helpers/dictionary";
import { cancelInvoiceReason } from "../../helpers/cancelInvoiceReason";
import { useRevalidateFetch } from "../../hooks/useRevalidateFetch";
import { useRuleValidator } from "../../hooks/useRuleValidator";
import { PERMISSIONS } from "../../router/permissions";
import { columns } from "./config";
import { InvoicesInputs } from "./InvoicesInputs";
import { EmailForm } from "./EmailForm";
import { CancelInvoiceModal } from "./CancelInvoiceModal";

const tableKey = "invoices";

const SectionHeader = ({ title, marginTop }) => (
  <Grid item xs={12} style={marginTop ? { marginTop: 8 } : undefined}>
    <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 6 }}>
      {title}
    </Typography>
    <Divider />
  </Grid>
);

export default function Invoices() {
  const dispatch = useDispatch();
  const { setSnackbar, buildTableConfig } = useSearch();

  const {
    invoices,
    formCancelInvoice,
    formSubstituteInvoice,
    invoiceId,
    sendEmailInvoice,
    paymentTypes,
    askForCancelMigration,
  } = useSelector(({ invoices }) => invoices);
  const { satUsages } = useSelector(({ creditNotes }) => creditNotes);
  useRevalidateFetch(getSatUsages, satUsages);
  const hasCancelMigrationPermission = useRuleValidator(PERMISSIONS.INVOICES_ERP_MIGRATION_CANCEL);

  useValidateReload(
    [
      "invoicesLoading",
      "downloadInvoicesLoading",
      "cancelInvoiceLoading",
      "substituteInvoiceLoading",
      "forwardEmailLoading",
      "downloadPromissoryNoteLoading",
    ],
    getInvoicesPaymentTypes,
    paymentTypes,
  );

  const { inputValues, handleNewValues, onChange, reset, errors } = useForm({
    payment: null,
    reason: cancelInvoiceReason?.[0] || null,
    email: "",
    emails: [],
    regime: null,
    satUsage: null,
    rfc: "",
    customer: "",
    CP: "",
    paymentType: null,
  });
  const { reason, email, payment, emails, regime, satUsage, rfc, customer, CP, paymentType } = inputValues;

  const { isLoadingComp, configTables } = useSelector(({ search }) => search);

  const [cancelNoteMigrations, setCancelNoteMigrations] = useState(false);

  const resetCancelForm = () => {
    handleNewValues({ reason: cancelInvoiceReason?.[0] || null });
    setCancelNoteMigrations(false);
  };

  const doCancelInvoice = () => {
    if (!reason?.value) {
      setSnackbar("Seleccione un motivo");
      return;
    }
    dispatch(
      cancelInvoice(
        invoiceId,
        { reason: reason.value, cancelNoteMigrations },
        resetCancelForm,
      ),
    );
  };
  const closeCancelModal = () => {
    dispatch(setFormCancelInvoice(false));
    setCancelNoteMigrations(false);
  };

  const selectedInvoice = useMemo(
    () => Array.isArray(invoices) ? invoices.find((inv) => inv.id === invoiceId) : null,
    [invoices, invoiceId],
  );

  const doSubstituteInvoice = () => {
    if (!payment?.satCode) return setSnackbar("Seleccione un método de pago");
    if (!regime?.value) return setSnackbar("Seleccione un régimen fiscal");
    if (!satUsage?.code) return setSnackbar("Seleccione un uso CFDI");
    if (!CP) return setSnackbar("Ingrese el código postal");
    dispatch(
      substituteInvoice(
        invoiceId,
        {
          payment: payment.satCode,
          taxRegime: regime.value,
          CFDIusage: satUsage.code,
          paymentType: paymentType?.value,
          CP,
          serie: selectedInvoice?.serie,
          folio: selectedInvoice?.folio,
          fiscalID: selectedInvoice?.fiscalID,
          rfc,
          customer,
        },
        payment.name,
      ),
    );
  };
  const closeSubstituteModal = () => dispatch(setFormSubstituteInvoice(false));

  useEffect(() => {
    if (formSubstituteInvoice && selectedInvoice) {
      handleNewValues({
        rfc: selectedInvoice.rfc || "",
        customer: selectedInvoice.customer || "",
        CP: selectedInvoice.CP || "",
        paymentType: paymentsInvoicesType.find((p) => p.value === (selectedInvoice.invoiceType === "ELECTRONICA_CREDITO" ? "PPD" : "PUE")) || null,
      });
    } else {
      handleNewValues({ payment: null, regime: null, satUsage: null, rfc: "", customer: "", CP: "", paymentType: null });
    }
  }, [formSubstituteInvoice, selectedInvoice]);

  useEffect(() => {
    if (!formSubstituteInvoice || !selectedInvoice) return;
    const matchedPayment = paymentTypes?.find(
      (pt) =>
        pt.satCode === selectedInvoice.payment ||
        pt.name === selectedInvoice.payment ||
        pt.label === selectedInvoice.payment,
    ) || null;
    const matchedRegime = regimensData.find(
      (r) => r.value === selectedInvoice.taxRegime,
    ) || null;
    const matchedSatUsage = satUsages?.find(
      (s) => s.code === selectedInvoice.CFDIusage,
    ) || null;
    handleNewValues({
      payment: matchedPayment,
      regime: matchedRegime,
      satUsage: matchedSatUsage,
    });
  }, [formSubstituteInvoice, selectedInvoice, paymentTypes, satUsages]);

  const handleEmailModal = () => dispatch(emailModal(false));

  const submitEmail = (e) => {
    e.preventDefault();
    dispatch(forwardEmail(invoiceId, { email: emails.join(", ") }, handleEmailModal));
  };

  useEffect(() => {
    !configTables[tableKey] && buildTableConfig(tableKey);
  }, []);

  return (
    <React.Fragment>
      <CardTable
        tableKey={tableKey}
        data={invoices}
        columns={columns}
        isLoading={isLoadingComp?.invoicesLoading}
        hasHeader
        title={"Listado de Facturas"}
        icon={<Receipt />}
        tableContent={<InvoicesInputs />}
        blockBtn
      />
      <CancelInvoiceModal
        open={formCancelInvoice}
        onClose={closeCancelModal}
        onSubmit={doCancelInvoice}
        isLoading={isLoadingComp?.cancelInvoiceLoading}
        disabledBtn={isLoadingComp?.checkNoteMigrationsLoading}
        reason={reason}
        handleNewValues={handleNewValues}
        showMigrationSwitch={askForCancelMigration && hasCancelMigrationPermission}
        cancelNoteMigrations={cancelNoteMigrations}
        setCancelNoteMigrations={setCancelNoteMigrations}
      />
      <ModalV2
        title="SUSTITUCIÓN DE FACTURA"
        open={formSubstituteInvoice}
        onClose={closeSubstituteModal}
        justConfirm
        onSubmit={doSubstituteInvoice}
        isLoading={isLoadingComp?.substituteInvoiceLoading}
        confirmText="Sustituir factura"
        maxWidth={600}
        padding={24}
      >
        {/* Factura original */}
        <SectionHeader title="Factura original" />
        <Grid item xs={6}>
          <TextField
            label="Serie"
            value={selectedInvoice?.serie || ""}
            variant="outlined"
            size="small"
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Folio"
            value={selectedInvoice?.folio || ""}
            variant="outlined"
            size="small"
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="UUID factura a sustituir"
            value={selectedInvoice?.fiscalID || ""}
            variant="outlined"
            size="small"
            fullWidth
            disabled
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() =>
                      navigator.clipboard.writeText(selectedInvoice?.fiscalID || "").catch(() => {})
                    }
                  >
                    <FileCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Datos del receptor */}
        <SectionHeader title="Datos del receptor" marginTop />
        <Grid item xs={12}>
          <TextField
            label="RFC"
            value={rfc}
            variant="outlined"
            size="small"
            fullWidth
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Nombre"
            value={customer}
            variant="outlined"
            size="small"
            fullWidth
            onChange={(e) => handleNewValues({ customer: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Código postal"
            value={CP}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{ maxLength: 5, inputMode: "numeric" }}
            onChange={(e) => handleNewValues({ CP: e.target.value })}
          />
        </Grid>

        {/* Datos fiscales */}
        <SectionHeader title="Datos fiscales" marginTop />
        <Grid item xs={6}>
          <AutocompleteComponent
            label="Método de pago"
            options={paymentTypes}
            name="payment"
            value={payment}
            handleValues={handleNewValues}
            keyOption="satCode"
            size="small"
          />
        </Grid>
        <Grid item xs={6}>
          <AutocompleteComponent
            label="Uso CFDI"
            options={satUsages ?? []}
            name="satUsage"
            value={satUsage}
            handleValues={handleNewValues}
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <AutocompleteComponent
            label="Tipo de pago"
            options={paymentsInvoicesType}
            name="paymentType"
            value={paymentType}
            handleValues={handleNewValues}
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <AutocompleteComponent
            label="Régimen fiscal"
            options={regimensData}
            name="regime"
            value={regime}
            handleValues={handleNewValues}
            size="small"
          />
        </Grid>
      </ModalV2>
      <ModalV2
        title="Reenviar Factura"
        open={sendEmailInvoice}
        onClose={handleEmailModal}
        justConfirm
        onSubmit={submitEmail}
        disabledBtn={emails.length === 0}
        isLoading={isLoadingComp?.forwardEmailLoading}
      >
        <EmailForm
          email={email}
          emails={emails}
          onChange={onChange}
          setValues={handleNewValues}
          reset={reset}
          errors={errors}
        />
      </ModalV2>
    </React.Fragment>
  );
}
